import type { ExtraPackage } from 'src/features/ExtraPackage/api/types'
import type { UANewbiePackSnapshot } from './useUANewbiePack'

/** Hard cap on cards rendered in the popup. */
const MAX_PACKS = 3

/**
 * Pick the one-time packs to surface in the newbie-pack popup.
 *
 * A pack is included iff:
 * 1. it has `oneTimePurchase: true`, AND
 * 2. its `id` matches a `snapshot.tiers[]` entry that is currently
 *    purchasable and not yet purchased.
 *
 * Returns an empty list while the snapshot is still loading
 * (`undefined`) or unavailable (`null`), so the popup never leaks
 * arbitrary one-time SKUs during the initial render.
 */
export function selectUANewbiePacks(
  extraPackages: ExtraPackage[] | undefined,
  snapshot: UANewbiePackSnapshot | null | undefined,
): ExtraPackage[] {
  if (!snapshot || !extraPackages) return []

  const eligibleIds = new Set(
    snapshot.tiers
      .filter(tier => tier.purchasable && !tier.purchased)
      .map(tier => tier.itemId),
  )
  if (eligibleIds.size === 0) return []

  return extraPackages
    .filter(
      pack =>
        pack.oneTimePurchase &&
        (eligibleIds.has(pack.id) ||
          (pack.legacyItemId != null && eligibleIds.has(pack.legacyItemId))),
    )
    .sort((a, b) => a.prices.USD - b.prices.USD)
    .slice(0, MAX_PACKS)
}
